import React, { useState } from "react";
import { Alert, Pressable, StyleSheet, Switch, Text, TextInput, View } from "react-native";
import { SubScreenShell } from "./SubScreenShell.js";
import { useLanguage } from "../i18n/LanguageContext.js";

/**
 * Minimal habit settings while Screen Time suite is parked.
 */
export function HabitSettingsScreen({
  settingsDraft,
  onChangeDraft,
  onSave,
  onToggleDailyCheckIn,
  onBack,
}) {
  const [saving, setSaving] = useState(false);
  const { t, locale, setLocale } = useLanguage();

  const save = async () => {
    const rt = (settingsDraft.reminderTime || "").trim();
    if (rt && !/^([01]\d|2[0-3]):[0-5]\d$/.test(rt)) {
      Alert.alert("Invalid time", "Please use HH:MM (e.g. 08:00).");
      return;
    }
    const gd = Number(settingsDraft.goalDays);
    if (!Number.isFinite(gd) || gd <= 0) {
      Alert.alert("Invalid goal", "Goal days must be a positive number.");
      return;
    }
    setSaving(true);
    try {
      await onSave({ ...settingsDraft, reminderTime: rt, goalDays: gd });
      Alert.alert("Saved", t("habit_saved"));
    } catch (e) {
      Alert.alert("Could not save", e?.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <SubScreenShell title={t("habit_settings_title")} onBack={onBack}>
      <Text style={styles.label}>{t("lang_label")}</Text>
      <View style={styles.langRow}>
        <Pressable
          style={[styles.langChip, locale === "en" && styles.langChipOn]}
          onPress={() => setLocale("en")}
        >
          <Text style={[styles.langChipText, locale === "en" && styles.langChipTextOn]}>{t("lang_english")}</Text>
        </Pressable>
        <Pressable
          style={[styles.langChip, locale === "fr" && styles.langChipOn]}
          onPress={() => setLocale("fr")}
        >
          <Text style={[styles.langChipText, locale === "fr" && styles.langChipTextOn]}>{t("lang_french")}</Text>
        </Pressable>
      </View>

      <Text style={styles.label}>{t("habit_name")}</Text>
      <TextInput
        value={settingsDraft.habitName || ""}
        onChangeText={(tVal) => onChangeDraft((s) => ({ ...s, habitName: tVal }))}
        placeholder="Procsystim"
        placeholderTextColor="#9AA0A6"
        style={styles.input}
        maxLength={50}
      />

      <Text style={styles.label}>{t("habit_goal_days")}</Text>
      <TextInput
        value={String(settingsDraft.goalDays ?? "")}
        onChangeText={(tVal) =>
          onChangeDraft((s) => ({
            ...s,
            goalDays: tVal === "" ? "" : Number(tVal.replace(/[^\d]/g, "")),
          }))
        }
        placeholder="90"
        placeholderTextColor="#9AA0A6"
        style={styles.input}
        keyboardType="number-pad"
      />

      <Text style={styles.label}>{t("habit_reminder")}</Text>
      <TextInput
        value={settingsDraft.reminderTime || ""}
        onChangeText={(tVal) => onChangeDraft((s) => ({ ...s, reminderTime: tVal }))}
        placeholder="08:00"
        placeholderTextColor="#9AA0A6"
        style={styles.input}
        maxLength={5}
      />

      <View style={styles.switchRow}>
        <View style={{ flex: 1, paddingRight: 12 }}>
          <Text style={styles.labelTight}>{t("habit_checkin")}</Text>
          <Text style={styles.hint}>{t("habit_checkin_hint")}</Text>
        </View>
        <Switch
          value={!!settingsDraft.dailyCheckInNotificationsEnabled}
          onValueChange={(v) => onToggleDailyCheckIn?.(v)}
          trackColor={{ false: "#D1D5DB", true: "#6EE7B7" }}
          thumbColor={settingsDraft.dailyCheckInNotificationsEnabled ? "#1ABC9C" : "#f4f3f4"}
        />
      </View>

      <Pressable style={[styles.saveBtn, saving && { opacity: 0.7 }]} onPress={() => void save()} disabled={saving}>
        <Text style={styles.saveBtnText}>{saving ? t("habit_saving") : t("habit_save")}</Text>
      </Pressable>
    </SubScreenShell>
  );
}

const styles = StyleSheet.create({
  label: { fontSize: 13, fontWeight: "700", color: "#374151", marginBottom: 6, marginTop: 12 },
  labelTight: { fontSize: 13, fontWeight: "700", color: "#374151" },
  hint: { fontSize: 12, color: "#6B7280", marginTop: 4, lineHeight: 17 },
  input: {
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 15,
    backgroundColor: "#FAFAFA",
    color: "#111",
  },
  langRow: { flexDirection: "row", gap: 8, marginBottom: 4 },
  langChip: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    backgroundColor: "#F9FAFB",
  },
  langChipOn: { borderColor: "#0D9488", backgroundColor: "#CCFBF1" },
  langChipText: { fontWeight: "700", color: "#6B7280", fontSize: 13 },
  langChipTextOn: { color: "#0F766E" },
  switchRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 18,
    marginBottom: 8,
  },
  saveBtn: {
    marginTop: 24,
    backgroundColor: "#0D9488",
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: "center",
  },
  saveBtnText: { color: "#fff", fontWeight: "800", fontSize: 15 },
});
